import React, { useContext, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import "./Hero.css";
import { Context } from "../../../context/Context";
import { landingContent } from "../content/landingContent";

// Relative [x%, y%] starting spots for the 18 floating balls, spread across
// the whole hero so the balls surround the headline instead of covering it.
const START_POSITIONS = [
  [0.07, 0.12], [0.19, 0.1], [0.32, 0.13], [0.46, 0.1], [0.61, 0.13], [0.76, 0.11],
  [0.9, 0.17], [0.11, 0.35], [0.28, 0.34], [0.46, 0.36], [0.65, 0.34], [0.84, 0.38],
  [0.08, 0.63], [0.23, 0.69], [0.4, 0.66], [0.58, 0.7], [0.75, 0.65], [0.91, 0.69],
];

const BALL_SIZES = [
  "xl", "lg", "sm", "sm", "sm", "sm", "md", "sm", "md", "sm", "md", "md",
  "lg", "sm", "sm", "sm", "lg", "sm",
];

// Balls cycle through 6 brand-tinted gradients (b1..b6).
const BALL_TONES = [1, 2, 3, 4, 5, 6, 1, 2, 3, 4, 5, 6, 1, 2, 3, 4, 5, 6];

export const Hero = () => {
  const { lang } = useContext(Context);
  const t = landingContent[lang] || landingContent.en;

  const heroRef = useRef(null);
  const ballEls = useRef([]);
  const physics = useRef([]);
  const rafRef = useRef(null);
  const dragRef = useRef(null);
  const prevPointRef = useRef(null);
  const lastTickRef = useRef(0);

  useEffect(() => {
    const hero = heroRef.current;
    if (!hero) return undefined;

    const reduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    const clamp = (b, w, h) => {
      if (b.x - b.r < 6) {
        b.x = b.r + 6;
        b.vx = Math.abs(b.vx) * 0.92;
      }
      if (b.x + b.r > w - 6) {
        b.x = w - b.r - 6;
        b.vx = -Math.abs(b.vx) * 0.92;
      }
      if (b.y - b.r < 6) {
        b.y = b.r + 6;
        b.vy = Math.abs(b.vy) * 0.92;
      }
      if (b.y + b.r > h - 6) {
        b.y = h - b.r - 6;
        b.vy = -Math.abs(b.vy) * 0.92;
      }
    };

    const setup = () => {
      const rect = hero.getBoundingClientRect();
      const w = rect.width;
      const h = rect.height;
      physics.current = ballEls.current.map((el, i) => {
        const r = el.offsetWidth / 2;
        const [px, py] = START_POSITIONS[i];
        return {
          el,
          r,
          x: w * px,
          y: h * py,
          vx: reduced ? 0 : (i % 2 ? 1 : -1) * (18 + (i % 4) * 5),
          vy: reduced ? 0 : ((i % 3) - 1) * 17,
          m: r * r,
          dragging: false,
        };
      });
      physics.current.forEach((b) => clamp(b, w, h));
      render();
    };

    const render = () => {
      physics.current.forEach((b) => {
        b.el.style.transform = `translate3d(${b.x - b.r}px, ${b.y - b.r}px, 0)`;
      });
    };

    const collide = (a, b) => {
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const d2 = dx * dx + dy * dy;
      const min = a.r + b.r;
      if (d2 <= 0 || d2 >= min * min) return;
      const d = Math.sqrt(d2);
      const nx = dx / d;
      const ny = dy / d;
      const overlap = min - d;
      if (!a.dragging && !b.dragging) {
        a.x -= (nx * overlap) / 2;
        a.y -= (ny * overlap) / 2;
        b.x += (nx * overlap) / 2;
        b.y += (ny * overlap) / 2;
      } else if (a.dragging && !b.dragging) {
        b.x += nx * overlap;
        b.y += ny * overlap;
      } else if (!a.dragging && b.dragging) {
        a.x -= nx * overlap;
        a.y -= ny * overlap;
      }
      const rvx = b.vx - a.vx;
      const rvy = b.vy - a.vy;
      const v = rvx * nx + rvy * ny;
      if (v > 0) return;
      const ia = a.dragging ? 0 : 1 / a.m;
      const ib = b.dragging ? 0 : 1 / b.m;
      const den = ia + ib;
      if (!den) return;
      const j = (-1.9 * v) / den;
      const ix = j * nx;
      const iy = j * ny;
      if (!a.dragging) {
        a.vx -= ix * ia;
        a.vy -= iy * ia;
      }
      if (!b.dragging) {
        b.vx += ix * ib;
        b.vy += iy * ib;
      }
    };

    const tick = (now) => {
      const rect = hero.getBoundingClientRect();
      const dt = Math.min(0.025, (now - lastTickRef.current) / 1000 || 0.016);
      lastTickRef.current = now;
      if (!reduced) {
        physics.current.forEach((b) => {
          if (!b.dragging) {
            b.x += b.vx * dt;
            b.y += b.vy * dt;
          }
          clamp(b, rect.width, rect.height);
        });
        for (let k = 0; k < 2; k++) {
          for (let i = 0; i < physics.current.length; i++) {
            for (let j = i + 1; j < physics.current.length; j++) {
              collide(physics.current[i], physics.current[j]);
            }
          }
        }
        render();
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    const point = (e) => {
      const rect = hero.getBoundingClientRect();
      return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };

    const cleanups = [];

    ballEls.current.forEach((el, i) => {
      const onDown = (e) => {
        const b = physics.current[i];
        dragRef.current = b;
        b.dragging = true;
        const p = point(e);
        prevPointRef.current = { ...p, t: performance.now() };
        b.vx = 0;
        b.vy = 0;
        try {
          el.setPointerCapture(e.pointerId);
        } catch (err) {
          // ignore
        }
        e.preventDefault();
      };
      const onMove = (e) => {
        const b = dragRef.current;
        if (!b || b.el !== el) return;
        const p = point(e);
        const now = performance.now();
        const dt = Math.max(8, now - prevPointRef.current.t) / 1000;
        const rect = hero.getBoundingClientRect();
        b.vx = Math.max(-850, Math.min(850, (p.x - prevPointRef.current.x) / dt));
        b.vy = Math.max(-850, Math.min(850, (p.y - prevPointRef.current.y) / dt));
        b.x = Math.max(b.r, Math.min(rect.width - b.r, p.x));
        b.y = Math.max(b.r, Math.min(rect.height - b.r, p.y));
        prevPointRef.current = { ...p, t: now };
        physics.current.forEach((other) => {
          if (other !== b) collide(b, other);
        });
        render();
        e.preventDefault();
      };
      const onUp = (e) => {
        const b = dragRef.current;
        if (!b || b.el !== el) return;
        b.dragging = false;
        dragRef.current = null;
        prevPointRef.current = null;
        try {
          el.releasePointerCapture(e.pointerId);
        } catch (err) {
          // ignore
        }
      };
      const onKeyDown = (e) => {
        const b = physics.current[i];
        if (!b) return;
        const step = e.shiftKey ? 30 : 14;
        let handled = true;
        if (e.key === "ArrowRight") b.x += step;
        else if (e.key === "ArrowLeft") b.x -= step;
        else if (e.key === "ArrowDown") b.y += step;
        else if (e.key === "ArrowUp") b.y -= step;
        else handled = false;
        if (handled) {
          const rect = hero.getBoundingClientRect();
          clamp(b, rect.width, rect.height);
          render();
          e.preventDefault();
        }
      };

      el.addEventListener("pointerdown", onDown);
      el.addEventListener("pointermove", onMove);
      el.addEventListener("pointerup", onUp);
      el.addEventListener("pointercancel", onUp);
      el.addEventListener("keydown", onKeyDown);

      cleanups.push(() => {
        el.removeEventListener("pointerdown", onDown);
        el.removeEventListener("pointermove", onMove);
        el.removeEventListener("pointerup", onUp);
        el.removeEventListener("pointercancel", onUp);
        el.removeEventListener("keydown", onKeyDown);
      });
    });

    let resizeTimer;
    const onResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(setup, 130);
    };
    window.addEventListener("resize", onResize);

    setup();
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      cancelAnimationFrame(rafRef.current);
      clearTimeout(resizeTimer);
      window.removeEventListener("resize", onResize);
      cleanups.forEach((fn) => fn());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  return (
    <section className="landing-hero" id="hero" ref={heroRef}>
      <div className="landing-hero-ambient a1" aria-hidden="true"></div>
      <div className="landing-hero-ambient a2" aria-hidden="true"></div>
      <div className="landing-hero-ambient a3" aria-hidden="true"></div>

      <div className="landing-hero-copy">
        <div className="landing-hero-lines">
          <h1>{t.hero.title}</h1>
          <h2>{t.hero.subtitle}</h2>
        </div>
        <div className="landing-hero-actions">
          <Link
            to="/contactus"
            className="landing-btn landing-btn-primary landing-hero-cta"
          >
            {t.hero.start}
          </Link>
          <Link to="/signin" className="landing-hero-login-cta">
            {t.hero.login}
          </Link>
        </div>
      </div>

      {t.hero.balls.map((label, i) => (
        <button
          type="button"
          key={label}
          ref={(el) => {
            ballEls.current[i] = el;
          }}
          className={`landing-ball b${BALL_TONES[i]} ${BALL_SIZES[i]}`}
        >
          {label}
        </button>
      ))}
    </section>
  );
};

export default Hero;
